import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Shield,
  MagnifyingGlass,
  ArrowClockwise,
  Eye,
  ClockCounterClockwise,
} from '@phosphor-icons/react';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});
API.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('pa_access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const StatTile = ({ label, value, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testId}>
    <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-black text-slate-900 mt-1">{value}</div>
  </div>
);

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  cancel: 'bg-amber-100 text-amber-800',
  login: 'bg-slate-100 text-slate-700',
  generate_links: 'bg-purple-100 text-purple-800',
};
const actionBadgeClass = (action) => {
  if (!action) return 'bg-slate-100 text-slate-700';
  const suffix = action.split('.')[1] || '';
  for (const [k, v] of Object.entries(ACTION_COLORS)) {
    if (suffix.includes(k)) return v;
  }
  return 'bg-slate-100 text-slate-700';
};

const formatTs = (iso, lang) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(lang === 'de' ? 'de-CH' : 'en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const AdminAudit = () => {
  const { language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);

  const [stats, setStats] = useState({ total: 0, by_actor: [], by_action: [], by_entity_type: [] });
  const [filters, setFilters] = useState({ actors: [], actions: [], entity_types: [] });
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [search, setSearch] = useState('');
  const [actorFilter, setActorFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [rangeDays, setRangeDays] = useState(30);
  const [detail, setDetail] = useState(null);

  const loadStats = async () => {
    try {
      const { data } = await API.get('/admin/audit-logs/stats', { params: { days: rangeDays } });
      setStats(data);
    } catch (e) {
      toast.error(L('Failed to load stats', 'Statistik konnte nicht geladen werden'));
    }
  };

  const loadFilters = async () => {
    try {
      const { data } = await API.get('/admin/audit-logs/filters');
      setFilters(data);
    } catch { /* silent */ }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      if (actorFilter !== 'all') params.actor_id = actorFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (rangeDays) {
        const start = new Date();
        start.setDate(start.getDate() - rangeDays);
        params.start = start.toISOString().slice(0, 10);
      }
      const { data } = await API.get('/admin/audit-logs', { params });
      setRows(data.items || []);
      setTotalRows(data.total || 0);
    } catch (e) {
      toast.error(L('Failed to load audit log', 'Audit-Log konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { loadStats(); loadRows(); }, [rangeDays]);
  // Filters other than range trigger reload immediately
  useEffect(() => { loadRows(); }, [actorFilter, actionFilter, entityFilter]);

  const handleSearchKey = (e) => {
    if (e.key === 'Enter') loadRows();
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const day = (r.timestamp || '').slice(0, 10);
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(r);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="admin-audit-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-slate-800 text-white flex items-center justify-center">
          <Shield size={22} weight="fill" />
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-900">{L('Audit Log', 'Audit-Log')}</h1>
          <p className="text-slate-600 text-sm">
            {L('Track every admin/staff change to bookings, users, cash register and more.',
               'Nachvollziehbar: alle Änderungen an Buchungen, Benutzern, Kasse und mehr.')}
          </p>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label={L(`Events (${rangeDays}d)`, `Ereignisse (${rangeDays} T)`)} value={stats.total} testId="stat-total" />
        <StatTile label={L('Unique actors', 'Aktive Nutzer')} value={stats.by_actor?.length || 0} testId="stat-actors" />
        <StatTile label={L('Distinct actions', 'Aktions-Arten')} value={stats.by_action?.length || 0} testId="stat-actions" />
        <StatTile label={L('Entity types', 'Objekt-Arten')} value={stats.by_entity_type?.length || 0} testId="stat-entities" />
      </div>

      {/* Top-3 breakdowns */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs uppercase font-bold text-slate-500 mb-3">{L('Top actors', 'Aktivste Nutzer')}</h3>
          <ul className="space-y-2 text-sm">
            {(stats.by_actor || []).slice(0, 5).map((a) => (
              <li key={a.key} className="flex justify-between">
                <span className="text-slate-700 font-medium truncate">{a.key}</span>
                <span className="font-bold text-slate-900">{a.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs uppercase font-bold text-slate-500 mb-3">{L('Top actions', 'Häufigste Aktionen')}</h3>
          <ul className="space-y-2 text-sm">
            {(stats.by_action || []).slice(0, 5).map((a) => (
              <li key={a.key} className="flex justify-between">
                <span className="text-slate-700 font-mono text-xs">{a.key}</span>
                <span className="font-bold text-slate-900">{a.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs uppercase font-bold text-slate-500 mb-3">{L('Top entity types', 'Häufigste Objekte')}</h3>
          <ul className="space-y-2 text-sm">
            {(stats.by_entity_type || []).slice(0, 5).map((a) => (
              <li key={a.key} className="flex justify-between">
                <span className="text-slate-700 font-medium">{a.key}</span>
                <span className="font-bold text-slate-900">{a.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKey}
              placeholder={L('Search summary, actor, entity id…', 'Suche in Summary, Nutzer, Objekt-ID…')}
              className="pl-9"
              data-testid="audit-search"
            />
          </div>
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger data-testid="audit-actor-filter"><SelectValue placeholder={L('Actor', 'Nutzer')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('All actors', 'Alle Nutzer')}</SelectItem>
              {(filters.actors || []).map((a) => (
                <SelectItem key={a.id} value={a.id || '_'}>{a.email || a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="audit-action-filter"><SelectValue placeholder={L('Action', 'Aktion')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('All actions', 'Alle Aktionen')}</SelectItem>
              {(filters.actions || []).map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger data-testid="audit-entity-filter"><SelectValue placeholder={L('Entity', 'Objekt')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('All entities', 'Alle Objekte')}</SelectItem>
              {(filters.entity_types || []).map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500 font-semibold">{L('Range:', 'Zeitraum:')}</span>
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setRangeDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border ${
                rangeDays === d
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`range-${d}`}
            >
              {L(`${d}d`, `${d} T`)}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500">{L('Showing', 'Zeige')} {rows.length} / {totalRows}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { loadStats(); loadRows(); }}
              data-testid="audit-refresh"
            >
              <ArrowClockwise size={14} className="mr-1" />
              {L('Refresh', 'Aktualisieren')}
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">{L('Time', 'Zeit')}</TableHead>
              <TableHead className="w-48">{L('Actor', 'Nutzer')}</TableHead>
              <TableHead className="w-40">{L('Action', 'Aktion')}</TableHead>
              <TableHead className="w-24">{L('Entity', 'Objekt')}</TableHead>
              <TableHead>{L('Summary', 'Zusammenfassung')}</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">{L('Loading…', 'Wird geladen…')}</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400" data-testid="audit-empty">{L('No entries', 'Keine Einträge')}</TableCell></TableRow>
            )}
            {!loading && grouped.map(([day, dayRows]) => (
              <React.Fragment key={day}>
                <TableRow className="bg-slate-50">
                  <TableCell colSpan={6} className="py-1.5 text-xs font-bold text-slate-500 uppercase">
                    <ClockCounterClockwise size={12} className="inline mr-1" /> {day} · {dayRows.length}
                  </TableCell>
                </TableRow>
                {dayRows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(r)} data-testid={`audit-row-${r.id}`}>
                    <TableCell className="text-xs text-slate-600 font-mono whitespace-nowrap">
                      {(r.timestamp || '').slice(11, 19)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-semibold text-slate-900">{r.actor_name}</div>
                      <div className="text-xs text-slate-500">{r.actor_email}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${actionBadgeClass(r.action)} font-mono text-[10px]`}>{r.action}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">{r.entity_type}</TableCell>
                    <TableCell className="text-sm text-slate-700 truncate max-w-[400px]" title={r.summary}>{r.summary || '—'}</TableCell>
                    <TableCell><Eye size={16} className="text-slate-400" /></TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Badge className={`${actionBadgeClass(detail?.action)} font-mono text-[10px]`}>{detail?.action}</Badge>
              <span className="text-slate-500 font-normal text-sm">{detail?.entity_type}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase">{L('Time', 'Zeit')}</div>
                  <div className="font-mono">{formatTs(detail.timestamp, language)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase">{L('IP', 'IP')}</div>
                  <div className="font-mono">{detail.ip || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase">{L('Actor', 'Nutzer')}</div>
                  <div className="font-semibold">{detail.actor_name}</div>
                  <div className="text-xs text-slate-500">{detail.actor_email} · {detail.actor_role}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase">{L('Entity ID', 'Objekt-ID')}</div>
                  <div className="font-mono text-xs break-all">{detail.entity_id || '—'}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-semibold uppercase mb-1">{L('Summary', 'Zusammenfassung')}</div>
                <div className="p-3 bg-slate-50 rounded-lg text-sm">{detail.summary || '—'}</div>
              </div>
              {(detail.before || detail.after) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-red-600 font-semibold uppercase mb-1">{L('Before', 'Vorher')}</div>
                    <pre className="p-3 bg-red-50 border border-red-100 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">{detail.before ? JSON.stringify(detail.before, null, 2) : '—'}</pre>
                  </div>
                  <div>
                    <div className="text-xs text-emerald-700 font-semibold uppercase mb-1">{L('After', 'Nachher')}</div>
                    <pre className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">{detail.after ? JSON.stringify(detail.after, null, 2) : '—'}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAudit;
